import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Search, Camera } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc, collection, query as firestoreQuery, where, getDocs } from 'firebase/firestore';
import { storage, db, auth } from '../config/firebase';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();

  // Main Navigation
  const mainNavigation = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Messaging', path: '/messaging' },
    { name: 'Income Tracker', path: '/income' },
    { name: 'Clients', path: '/clients' },
    { name: 'Pets', path: '/pets' },
  ];

  // Vet Worker Tools (Safe features only - no medical calculations)
  const vetTools = [
    { name: '💊 Pill Counter', path: '/pill-counter' },
    { name: '📋 Quick Reference', path: '/quick-reference' },
    { name: '📝 Client Notes', path: '/client-notes' },
  ];

  // Productivity Tools
  const productivityTools = [
    { name: '✅ Shift Tasks', path: '/shift-tasks' },
    { name: '⏱️ Hours Tracker', path: '/hours-tracker' },
    { name: '📚 Study Notes', path: '/study-notes' },
  ];

  // Settings
  const settingsNav = [
    { name: '⚙️ Settings', path: '/settings' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      setUploadingPhoto(true);
      
      // Upload to Firebase Storage
      const fileName = `profile-photos/${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update auth profile
      await updateProfile(auth.currentUser, {
        photoURL: downloadURL
      });

      // Update Firestore user document (use setDoc with merge to be safe)
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        updatedAt: new Date().toISOString()
      });

      // Refresh current page using React Router (no full reload)
      alert('✅ Profile photo updated!');
      navigate(location.pathname, { replace: true });
      window.location.reload(); // Needed to refresh auth state
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('❌ Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const renderNavSection = (items, title) => (
    <div className="mb-4">
      {title && (
        <p className="px-4 text-xs font-semibold text-brand-brown/50 uppercase tracking-wider mb-2">
          {title}
        </p>
      )}
      <nav className="space-y-1">
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`block px-4 py-2.5 rounded-xl font-medium transition-colors ${
                isActive
                  ? 'bg-pastel-purple text-brand-brown'
                  : 'text-brand-brown/70 hover:bg-pastel-pink/30 hover:text-brand-brown'
              }`}
            >
              {item.name}
            </Link>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-pastel-pink/20">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-brand-brown/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Hamburger Menu */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-xl hover:bg-pastel-pink/30 transition-colors"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6 text-brand-brown" />
              ) : (
                <Menu className="h-6 w-6 text-brand-brown" />
              )}
            </button>

            {/* Center: Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="PetPages Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-bold text-brand-brown hidden sm:block">
                PetPages
              </span>
            </Link>

            {/* Right: Search and Profile */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSearchOpen(true)}
                className="p-2 rounded-xl hover:bg-pastel-pink/30 transition-colors"
              >
                <Search className="h-5 w-5 text-brand-brown" />
              </button>
              
              <div className="flex items-center space-x-2">
                {/* Profile Picture Upload */}
                <div className="relative group">
                  <label className="cursor-pointer block">
                    {currentUser?.photoURL || userProfile?.photoURL ? (
                      <img
                        src={currentUser.photoURL || userProfile.photoURL}
                        alt="Profile"
                        className="h-10 w-10 rounded-full object-cover ring-2 ring-pastel-purple"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-pastel-purple flex items-center justify-center ring-2 ring-pastel-purple">
                        <span className="text-white font-bold text-sm">
                          {(userProfile?.displayName || currentUser?.email || 'U')[0].toUpperCase()}
                        </span>
                      </div>
                    )}
                    
                    {/* Upload Overlay */}
                    <div className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {uploadingPhoto ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <Camera className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                </div>

                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-brand-brown">
                    {userProfile?.displayName || currentUser?.email}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="text-xs text-brand-brown/60 hover:text-brand-brown flex items-center space-x-1"
                  >
                    <LogOut className="h-3 w-3" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}

      {/* Sidebar Navigation */}
      <div
        className={`fixed inset-0 bg-black/50 z-30 transition-opacity duration-300 ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />
      
      <div
        className={`fixed left-0 top-0 bottom-0 w-72 bg-white shadow-xl z-40 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="PetPages Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h2 className="text-xl font-bold text-brand-brown">PetPages</h2>
                <p className="text-xs text-brand-brown/60">Pet Care Pro</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-xl hover:bg-pastel-pink/30 transition-colors"
            >
              <X className="h-5 w-5 text-brand-brown" />
            </button>
          </div>

          {/* Navigation Sections */}
          {renderNavSection(mainNavigation)}
          
          <div className="border-t border-brand-brown/10 my-4"></div>
          {renderNavSection(vetTools, 'Vet Tools')}
          
          <div className="border-t border-brand-brown/10 my-4"></div>
          {renderNavSection(productivityTools, 'Productivity')}
          
          <div className="border-t border-brand-brown/10 my-4"></div>
          {renderNavSection(settingsNav)}

          {/* Profile Section in Sidebar */}
          <div className="mt-6 pt-6 border-t border-brand-brown/10">
            <div className="flex items-center space-x-3">
              {currentUser?.photoURL || userProfile?.photoURL ? (
                <img
                  src={currentUser.photoURL || userProfile.photoURL}
                  alt="Profile"
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-pastel-purple flex items-center justify-center">
                  <span className="text-white font-bold">
                    {(userProfile?.displayName || currentUser?.email || 'U')[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <p className="text-sm font-medium text-brand-brown">
                  {userProfile?.displayName || currentUser?.email}
                </p>
                <p className="text-xs text-brand-brown/60 capitalize">
                  {userProfile?.userType || 'User'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

// Search Modal Component
const SearchModal = ({ onClose }) => {
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    clients: [],
    pets: [],
    appointments: []
  });
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const performSearch = async (query) => {
    if (!query.trim() || !currentUser) {
      setSearchResults({ clients: [], pets: [], appointments: [] });
      return;
    }

    setSearching(true);
    try {
      const lowerQuery = query.toLowerCase();

      // Search clients
      const clientsRef = collection(db, 'clients');
      const clientsQ = firestoreQuery(clientsRef, where('sitterId', '==', currentUser.uid));
      const clientsSnapshot = await getDocs(clientsQ);
      const clients = clientsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(client => {
          const fullName = client.name || `${client.firstName || ''} ${client.lastName || ''}`.trim();
          const email = client.email || '';
          return fullName.toLowerCase().includes(lowerQuery) ||
                 email.toLowerCase().includes(lowerQuery) ||
                 (client.firstName || '').toLowerCase().includes(lowerQuery) ||
                 (client.lastName || '').toLowerCase().includes(lowerQuery);
        });

      // Search pets
      const petsRef = collection(db, 'pets');
      const petsQ = firestoreQuery(petsRef, where('sitterId', '==', currentUser.uid));
      const petsSnapshot = await getDocs(petsQ);
      const pets = petsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(pet => 
          pet.name?.toLowerCase().includes(lowerQuery) ||
          pet.breed?.toLowerCase().includes(lowerQuery)
        );

      // Search appointments
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsQ = firestoreQuery(appointmentsRef, where('userId', '==', currentUser.uid));
      const appointmentsSnapshot = await getDocs(appointmentsQ);
      const appointments = appointmentsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(apt => 
          apt.clientName?.toLowerCase().includes(lowerQuery) ||
          apt.petName?.toLowerCase().includes(lowerQuery) ||
          apt.serviceType?.toLowerCase().includes(lowerQuery)
        );

      setSearchResults({ clients, pets, appointments });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  const handleResultClick = (type) => {
    if (type === 'client') navigate('/clients');
    else if (type === 'pet') navigate('/pets');
    else if (type === 'appointment') navigate('/dashboard');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-20">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-brand-brown/10">
          <div className="flex items-center space-x-3">
            <Search className="h-6 w-6 text-brand-brown/60" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search clients, pets, appointments..."
              className="flex-1 text-lg outline-none text-brand-brown"
              autoFocus
            />
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-brand-brown" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh] p-6">
          {searching ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple"></div>
            </div>
          ) : !searchQuery ? (
            <div className="text-center py-12 text-brand-brown/60">
              <Search className="h-12 w-12 mx-auto mb-3 text-brand-brown/30" />
              <p>Start typing to search...</p>
              <p className="text-sm mt-2">Search for clients, pets, or appointments</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Clients Results */}
              {searchResults.clients.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-brand-brown/70 mb-3">
                    Clients ({searchResults.clients.length})
                  </h3>
                  <div className="space-y-2">
                    {searchResults.clients.map(client => (
                      <button
                        key={client.id}
                        onClick={() => handleResultClick('client')}
                        className="w-full p-3 rounded-lg hover:bg-pastel-purple/20 text-left transition-colors"
                      >
                        <p className="font-medium text-brand-brown">
                          {client.name || `${client.firstName || ''} ${client.lastName || ''}`.trim()}
                        </p>
                        <p className="text-sm text-brand-brown/60">{client.email}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pets Results */}
              {searchResults.pets.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-brand-brown/70 mb-3">
                    Pets ({searchResults.pets.length})
                  </h3>
                  <div className="space-y-2">
                    {searchResults.pets.map(pet => (
                      <button
                        key={pet.id}
                        onClick={() => handleResultClick('pet')}
                        className="w-full p-3 rounded-lg hover:bg-pastel-pink/20 text-left transition-colors"
                      >
                        <p className="font-medium text-brand-brown">{pet.name}</p>
                        <p className="text-sm text-brand-brown/60">
                          {pet.breed} • {pet.type}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Appointments Results */}
              {searchResults.appointments.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-brand-brown/70 mb-3">
                    Appointments ({searchResults.appointments.length})
                  </h3>
                  <div className="space-y-2">
                    {searchResults.appointments.slice(0, 5).map(apt => (
                      <button
                        key={apt.id}
                        onClick={() => handleResultClick('appointment')}
                        className="w-full p-3 rounded-lg hover:bg-pastel-blue/20 text-left transition-colors"
                      >
                        <p className="font-medium text-brand-brown">
                          {apt.petName} - {apt.clientName}
                        </p>
                        <p className="text-sm text-brand-brown/60">
                          {apt.date} • {apt.serviceType}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchResults.clients.length === 0 &&
               searchResults.pets.length === 0 &&
               searchResults.appointments.length === 0 && (
                <div className="text-center py-12 text-brand-brown/60">
                  <p>No results found for "{searchQuery}"</p>
                  <p className="text-sm mt-2">Try a different search term</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Layout;
